import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi } from '../../services/api';

const AdminContext = createContext(null);

export const AdminProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('adminUser') || 'null');
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => Boolean(localStorage.getItem('adminToken')));

  const saveAdminSession = (payload) => {
    const token = payload?.token || payload?.accessToken || payload?.data?.token || payload?.data?.accessToken;
    const admin = payload?.admin || payload?.user || payload?.data?.admin || payload?.data?.user;

    if (!token || !admin) {
      throw new Error('Invalid admin login response');
    }

    localStorage.setItem('adminToken', token);
    const refreshToken = payload?.refreshToken || payload?.data?.refreshToken;
    if (refreshToken) localStorage.setItem('adminRefreshToken', refreshToken);
    localStorage.setItem('adminUser', JSON.stringify(admin));
    localStorage.setItem('fosen_access_token', token);
    if (refreshToken) localStorage.setItem('fosen_refresh_token', refreshToken);
    localStorage.setItem('fosen_user', JSON.stringify(admin));
    setUser(admin);
  };

  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem('adminRefreshToken') || localStorage.getItem('fosen_refresh_token');
    if (refreshToken) authApi.logout({ refreshToken }).catch(() => {});
    setUser(null);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminRefreshToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('fosen_access_token');
    localStorage.removeItem('fosen_refresh_token');
    localStorage.removeItem('fosen_user');
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const refreshToken = localStorage.getItem('adminRefreshToken') || localStorage.getItem('fosen_refresh_token');
    if (!token && !refreshToken) {
      setUser(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    try {
      let response;
      try {
        response = await authApi.adminProfile();
      } catch (error) {
        if (!refreshToken) throw error;
        const refreshResponse = await authApi.refresh(refreshToken);
        const nextAccessToken = refreshResponse.data?.data?.accessToken;
        if (!nextAccessToken) throw error;
        localStorage.setItem('adminToken', nextAccessToken);
        localStorage.setItem('fosen_access_token', nextAccessToken);
        response = await authApi.adminProfile();
      }
      const admin = response.data?.admin || response.data?.data;
      localStorage.setItem('adminUser', JSON.stringify(admin));
      localStorage.setItem('fosen_user', JSON.stringify(admin));
      setUser(admin);
      return admin;
    } catch (error) {
      if (!refreshToken) logout();
      return null;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (payload) => {
    setLoading(true);
    try {
      const response = await authApi.adminLogin(payload);
      if (response.data?.requiresOtp) return response.data;
      saveAdminSession(response.data);
      return response.data?.admin || response.data?.data?.admin || response.data?.data?.user;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (payload) => {
    setLoading(true);
    try {
      const response = await authApi.registerAdmin(payload);
      if (response.data?.requiresOtp) return response.data;
      saveAdminSession(response.data);
      return response.data?.admin || response.data?.data?.admin || response.data?.data?.user;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminContext.Provider value={{ user, loading, login, signup, logout, checkAuth, setUser }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
};
